import React from 'react';
import { useUploadStatus } from '../contexts/UploadStatusContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useLocation } from 'react-router-dom';
import { FaYoutube, FaMap, FaTimes, FaCloudUploadAlt, FaCog, FaVideo, FaCheckCircle } from 'react-icons/fa';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import './StatusBar.css';

const StatusBar = () => {
  const {
    saveRun,
    cloudSyncRun,
    youtubeUpload,
    goproExtraction,
    genericTasks,
    exportVideo,
    clearExportVideo,
    cancelYoutubeUpload,
    cancelGoproExtraction,
  } = useUploadStatus();
  const { isCollapsed } = useSidebar();
  const location = useLocation();

  const sidebarPages = ['/home', '/my-rides', '/ride', '/compare', '/prediction', '/help', '/profile', '/dashboard', '/buy-credits', '/create-run'];
  const showSidebar = sidebarPages.some(path => location.pathname.startsWith(path));
  const hasActiveOperations =
    saveRun.active ||
    cloudSyncRun.active ||
    youtubeUpload.active ||
    goproExtraction.active ||
    genericTasks.length > 0 ||
    exportVideo.active;

  if (!hasActiveOperations) {
    return null;
  }

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatTimeMMSS = (seconds) => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleCancelYoutubeUpload = async () => {
    const runId = cancelYoutubeUpload();
    if (runId) {
      // Update Firestore to save only filename, no videoUrl
      try {
        const runRef = doc(db, 'runs', runId);
        await updateDoc(runRef, {
          videoUrl: null,
          uploadedToYouTube: false,
          uploadCancelled: true,
        });
        console.log('✅ Upload cancelled, saved with filename only');
      } catch (error) {
        console.error('Error updating Firestore after cancellation:', error);
      }
    }
  };

  const handleCancelGoproExtraction = () => {
    cancelGoproExtraction();
  };

  return (
    <div className="status-bar">
      <div className={`status-bar-content ${showSidebar ? 'with-sidebar' : ''} ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        {saveRun.active && (
          <div className="status-item status-item-message">
            <FaCloudUploadAlt className="status-icon" />
            <div className="status-info">
              <span className="status-label">{saveRun.label}</span>
            </div>
          </div>
        )}

        {cloudSyncRun.active && (
          <div className="status-item status-item-message">
            <FaCloudUploadAlt className="status-icon" />
            <div className="status-info">
              <span className="status-label">{cloudSyncRun.label}</span>
            </div>
          </div>
        )}

        {exportVideo.active && (
          <div className="status-item status-item-export">
            {exportVideo.phase === 'complete' ? (
              <FaCheckCircle className="status-icon status-icon-success" />
            ) : (
              <FaVideo className="status-icon" />
            )}
            <div className="status-info">
              <div className="status-label-row">
                <span className="status-label">
                  {exportVideo.phase === 'loading' && 'Export: Loading video…'}
                  {exportVideo.phase === 'recording' && (
                    <>
                      Export: Recording…
                      {exportVideo.duration != null && (
                        <span className="status-export-meta">
                          {' '}({formatTimeMMSS(exportVideo.currentTime)} / {formatTimeMMSS(exportVideo.duration)}
                          {exportVideo.frameIndex != null && `, frame ${exportVideo.frameIndex}`})
                        </span>
                      )}
                    </>
                  )}
                  {exportVideo.phase === 'uploading' && 'Export: Uploading…'}
                  {exportVideo.phase === 'processing' && (
                    <>Export: {exportVideo.statusMessage || 'Processing…'}</>
                  )}
                  {exportVideo.phase === 'complete' && 'Export ready'}
                  {exportVideo.phase === 'failed' && `Export failed: ${exportVideo.error || 'Unknown error'}`}
                </span>
                <div className="status-details">
                  {exportVideo.phase === 'processing' && exportVideo.progress != null && (
                    <>
                      <span className="status-progress">{Math.round(exportVideo.progress)}%</span>
                    </>
                  )}
                  {exportVideo.phase === 'recording' && exportVideo.progress != null && (
                    <>
                      <span className="status-progress">{Math.round(exportVideo.progress)}%</span>
                      {exportVideo.estimatedTimeRemaining != null && exportVideo.estimatedTimeRemaining > 0 && (
                        <span className="status-time">~{formatTime(exportVideo.estimatedTimeRemaining)} left</span>
                      )}
                    </>
                  )}
                  {exportVideo.phase === 'complete' && exportVideo.downloadURL && (
                    <a
                      href={exportVideo.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="status-export-open"
                    >
                      Open
                    </a>
                  )}
                </div>
              </div>
              {(exportVideo.phase === 'recording' || exportVideo.phase === 'uploading' || exportVideo.phase === 'processing') && exportVideo.progress != null && (
                <div className="status-progress-bar">
                  <div className="status-progress-fill" style={{ width: `${exportVideo.progress}%` }} />
                </div>
              )}
            </div>
            <button
              type="button"
              className="status-cancel-button"
              onClick={clearExportVideo}
              title="Dismiss"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {genericTasks.map((task) => (
          <div key={task.id} className="status-item">
            <FaCog className="status-icon" />
            <div className="status-info">
              <div className="status-label-row">
                <span className="status-label">{task.label}</span>
                <div className="status-details">
                  {task.progress != null && <span className="status-progress">{task.progress}%</span>}
                  {task.estimatedTimeRemaining != null && (
                    <span className="status-time">~{formatTime(task.estimatedTimeRemaining)} remaining</span>
                  )}
                </div>
              </div>
              {task.progress != null && (
                <div className="status-progress-bar">
                  <div className="status-progress-fill" style={{ width: `${task.progress}%` }} />
                </div>
              )}
            </div>
            {task.cancel && (
              <button className="status-cancel-button" onClick={task.cancel} title="Cancel">
                <FaTimes />
              </button>
            )}
          </div>
        ))}

        {youtubeUpload.active && (
          <div className="status-item">
            <FaYoutube className="status-icon" />
            <div className="status-info">
              <div className="status-label-row">
                <span className="status-label">
                  Uploading video to cloud: {youtubeUpload.fileName || 'video'}
                </span>
                <div className="status-details">
                  <span className="status-progress">{youtubeUpload.progress}%</span>
                  {youtubeUpload.estimatedTimeRemaining && (
                    <span className="status-time">
                      ~{formatTime(youtubeUpload.estimatedTimeRemaining)} remaining
                    </span>
                  )}
                </div>
              </div>
              <div className="status-progress-bar">
                <div 
                  className="status-progress-fill" 
                  style={{ width: `${youtubeUpload.progress}%` }}
                ></div>
              </div>
            </div>
            <button 
              className="status-cancel-button"
              onClick={handleCancelYoutubeUpload}
              title="Cancel upload"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {goproExtraction.active && (
          <div className="status-item">
            <FaMap className="status-icon" />
            <div className="status-info">
              <div className="status-label-row">
                <span className="status-label">
                  Extracting GPS data from video...
                </span>
                <div className="status-details">
                  <span className="status-progress">{goproExtraction.progress}%</span>
                  {goproExtraction.estimatedTimeRemaining && (
                    <span className="status-time">
                      ~{formatTime(goproExtraction.estimatedTimeRemaining)} remaining
                    </span>
                  )}
                </div>
              </div>
              <div className="status-progress-bar">
                <div 
                  className="status-progress-fill" 
                  style={{ width: `${goproExtraction.progress}%` }}
                ></div>
              </div>
            </div>
            <button 
              className="status-cancel-button"
              onClick={handleCancelGoproExtraction}
              title="Cancel extraction"
            >
              <FaTimes />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusBar;

